import { supabase } from '../lib/supabase';

// Helper function to calculate salary based on tipo and peso
const calculateSalary = (tipo, pesoLoncheado) => {
  if (tipo === 'EMPLATADO') {
    return 25;
  }
  if (pesoLoncheado < 10) {
    return 20;
  }
  return 25;
};

// Helper function to get week date range
const getWeekDateRange = (year, weekNumber) => {
  const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7);
  const dow = simple?.getDay();
  const ISOweekStart = simple;
  if (dow <= 4)
    ISOweekStart?.setDate(simple?.getDate() - simple?.getDay() + 1);
  else
    ISOweekStart?.setDate(simple?.getDate() + 8 - simple?.getDay());
  
  const start = new Date(ISOweekStart);
  start?.setHours(0, 0, 0, 0);
  const end = new Date(ISOweekStart);
  end?.setDate(end?.getDate() + 6);
  end?.setHours(23, 59, 59, 999);
  
  return { start, end };
};

// Helper to get quarter date range
const getQuarterDateRange = (year, quarter) => {
  const quarterMap = {
    Q1: { startMonth: 0, endMonth: 2 },
    Q2: { startMonth: 3, endMonth: 5 },
    Q3: { startMonth: 6, endMonth: 8 },
    Q4: { startMonth: 9, endMonth: 11 }
  };
  const { startMonth, endMonth } = quarterMap?.[quarter] || quarterMap?.Q1;
  const start = new Date(parseInt(year), startMonth, 1);
  start?.setHours(0, 0, 0, 0);
  const end = new Date(parseInt(year), endMonth + 1, 0);
  end?.setHours(23, 59, 59, 999);
  return { start, end };
};

// Helper to resolve date range from filters
const resolveDateRange = (filters) => {
  let dateFrom = null;
  let dateTo = null;
  const year = filters?.year ? parseInt(filters?.year) : new Date()?.getFullYear();

  if (filters?.dateFrom && filters?.dateTo) {
    dateFrom = new Date(filters.dateFrom);
    dateFrom?.setHours(0, 0, 0, 0);
    dateTo = new Date(filters.dateTo);
    dateTo?.setHours(23, 59, 59, 999);
  } else if (filters?.singleDay) {
    dateFrom = new Date(filters.singleDay);
    dateFrom?.setHours(0, 0, 0, 0);
    dateTo = new Date(filters.singleDay);
    dateTo?.setHours(23, 59, 59, 999);
  } else if (filters?.periodType === 'week' && filters?.week) {
    const range = getWeekDateRange(year, parseInt(filters?.week));
    dateFrom = range?.start;
    dateTo = range?.end;
  } else if (filters?.periodType === 'quarter' && filters?.quarter) {
    const range = getQuarterDateRange(year, filters?.quarter);
    dateFrom = range?.start;
    dateTo = range?.end;
  } else if (filters?.periodType === 'year') {
    dateFrom = new Date(year, 0, 1);
    dateFrom?.setHours(0, 0, 0, 0);
    dateTo = new Date(year, 11, 31);
    dateTo?.setHours(23, 59, 59, 999);
  } else if (filters?.month) {
    const [y, m] = filters?.month?.split('-');
    dateFrom = new Date(parseInt(y), parseInt(m) - 1, 1);
    dateFrom?.setHours(0, 0, 0, 0);
    dateTo = new Date(parseInt(y), parseInt(m), 0);
    dateTo?.setHours(23, 59, 59, 999);
  }

  return { dateFrom, dateTo };
};

export const executiveDashboardService = {
  // Get KPIs with filters
  async getKPIs(filters = {}) {
    try {
      const { dateFrom, dateTo } = resolveDateRange(filters);

      // Get total clients
      let clientsQuery = supabase?.from('clientes')?.select('*', { count: 'exact', head: true });
      const { count: totalClients } = await clientsQuery;

      // Get active employees
      let employeesQuery = supabase?.from('empleados')?.select('*', { count: 'exact', head: true })?.eq('activo', true);
      const { count: activeEmployees } = await employeesQuery;

      // Use rentabilidad_por_cliente_final view for accurate calculations
      let rentabilidadQuery = supabase?.from('rentabilidad_por_cliente_final')?.select('numero_loncheados, facturacion_total, coste_salarial_total, beneficio_neto');
      
      if (dateFrom && dateTo) {
        const dateFromStr = dateFrom?.toISOString()?.split('T')?.[0];
        const dateToStr = dateTo?.toISOString()?.split('T')?.[0];
        rentabilidadQuery = rentabilidadQuery?.gte('fecha', dateFromStr)?.lte('fecha', dateToStr);
      }

      const { data: rentabilidadData, error: rentabilidadError } = await rentabilidadQuery;
      if (rentabilidadError) throw rentabilidadError;

      // Aggregate data from rentabilidad view
      let totalLoncheados = rentabilidadData?.reduce((sum, r) => sum + (parseInt(r?.numero_loncheados) || 0), 0) || 0;
      let totalRevenue = rentabilidadData?.reduce((sum, r) => sum + (parseFloat(r?.facturacion_total) || 0), 0) || 0;
      const totalSalaries = rentabilidadData?.reduce((sum, r) => sum + (parseFloat(r?.coste_salarial_total) || 0), 0) || 0;

      return {
        totalClients: totalClients || 0,
        activeEmployees: activeEmployees || 0,
        totalLoncheados,
        totalSalaries,
        totalRevenue
      };
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      throw error;
    }
  },

  // Get client rankings by revenue
  async getClientRankings(filters = {}) {
    try {
      const { dateFrom, dateTo } = resolveDateRange(filters);

      // Use rentabilidad_por_cliente_final view for accurate revenue data
      let query = supabase?.from('rentabilidad_por_cliente_final')?.select('cliente_id, cliente_nombre, facturacion_total, numero_loncheados');
      
      if (dateFrom && dateTo) {
        const dateFromStr = dateFrom?.toISOString()?.split('T')?.[0];
        const dateToStr = dateTo?.toISOString()?.split('T')?.[0];
        query = query?.gte('fecha', dateFromStr)?.lte('fecha', dateToStr);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Group by cliente and aggregate revenue
      const clienteStats = {};
      data?.forEach(row => {
        if (row?.cliente_id) {
          if (!clienteStats?.[row?.cliente_id]) {
            clienteStats[row.cliente_id] = {
              clienteId: row?.cliente_id,
              nombre: row?.cliente_nombre || 'Cliente Desconocido',
              totalRevenue: 0,
              count: 0
            };
          }
          clienteStats[row.cliente_id].totalRevenue += parseFloat(row?.facturacion_total) || 0;
          clienteStats[row.cliente_id].count += parseInt(row?.numero_loncheados) || 0;
        }
      });

      // Convert to array and sort by revenue
      const rankings = Object.values(clienteStats)?.sort((a, b) => b?.totalRevenue - a?.totalRevenue)?.slice(0, 10);

      return rankings;
    } catch (error) {
      console.error('Error fetching client rankings:', error);
      throw error;
    }
  },

  // Get employee rankings by salaries and loncheados
  async getEmployeeRankings(filters = {}) {
    try {
      const { dateFrom, dateTo } = resolveDateRange(filters);

      let query = supabase?.from('loncheados')?.select('empleado_id, peso_loncheado, tipo, empleados(nombre)');
      
      if (dateFrom && dateTo) {
        query = query?.gte('created_at', dateFrom?.toISOString())?.lte('created_at', dateTo?.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      // Group by empleado
      const empleadoStats = {};
      data?.forEach(l => {
        if (l?.empleado_id) {
          if (!empleadoStats?.[l?.empleado_id]) {
            empleadoStats[l.empleado_id] = {
              empleadoId: l?.empleado_id,
              nombre: l?.empleados?.nombre || 'Empleado Desconocido',
              totalSalary: 0,
              loncheadosCount: 0
            };
          }
          const salary = calculateSalary(l?.tipo, parseFloat(l?.peso_loncheado) || 0);
          empleadoStats[l.empleado_id].totalSalary += salary;
          empleadoStats[l.empleado_id].loncheadosCount += 1;
        }
      });

      // Convert to array and sort by salary
      const rankings = Object.values(empleadoStats)?.sort((a, b) => b?.totalSalary - a?.totalSalary)?.slice(0, 10);

      return rankings;
    } catch (error) {
      console.error('Error fetching employee rankings:', error);
      throw error;
    }
  },

  // Get evolution data for charts (grouped by day/week/month)
  async getEvolutionData(filters = {}, groupBy = 'day') {
    try {
      const { dateFrom, dateTo } = resolveDateRange(filters);

      // Use rentabilidad_por_cliente_final view for accurate data
      let query = supabase?.from('rentabilidad_por_cliente_final')?.select('fecha, numero_loncheados, facturacion_total, coste_salarial_total');
      
      if (dateFrom && dateTo) {
        const dateFromStr = dateFrom?.toISOString()?.split('T')?.[0];
        const dateToStr = dateTo?.toISOString()?.split('T')?.[0];
        query = query?.gte('fecha', dateFromStr)?.lte('fecha', dateToStr);
      }

      query = query?.order('fecha', { ascending: true });

      const { data, error } = await query;
      if (error) throw error;

      // Group data by period
      const grouped = {};
      data?.forEach(row => {
        const date = new Date(row.fecha);
        let key;

        if (groupBy === 'day') {
          key = date?.toISOString()?.split('T')?.[0];
        } else if (groupBy === 'week') {
          const weekNum = Math.ceil((date?.getDate()) / 7);
          key = `Sem ${weekNum}`;
        } else if (groupBy === 'month') {
          const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
          key = monthNames?.[date?.getMonth()];
        }

        if (!grouped?.[key]) {
          grouped[key] = {
            period: key,
            loncheados: 0,
            salaries: 0,
            revenue: 0
          };
        }

        grouped[key].loncheados += parseInt(row?.numero_loncheados) || 0;
        grouped[key].revenue += parseFloat(row?.facturacion_total) || 0;
        grouped[key].salaries += parseFloat(row?.coste_salarial_total) || 0;
      });

      return Object.values(grouped);
    } catch (error) {
      console.error('Error fetching evolution data:', error);
      throw error;
    }
  },

  // Get comparative data for top clients
  async getComparativeData(filters = {}, limit = 5) {
    try {
      const { dateFrom, dateTo } = resolveDateRange(filters);

      // Use rentabilidad_por_cliente_final view
      let query = supabase?.from('rentabilidad_por_cliente_final')?.select('cliente_id, cliente_nombre, facturacion_total, coste_salarial_total');
      
      if (dateFrom && dateTo) {
        const dateFromStr = dateFrom?.toISOString()?.split('T')?.[0];
        const dateToStr = dateTo?.toISOString()?.split('T')?.[0];
        query = query?.gte('fecha', dateFromStr)?.lte('fecha', dateToStr);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Aggregate by client
      const clientData = {};
      data?.forEach(row => {
        if (!clientData?.[row?.cliente_id]) {
          clientData[row.cliente_id] = {
            name: row?.cliente_nombre,
            revenue: 0,
            costs: 0
          };
        }
        clientData[row.cliente_id].revenue += parseFloat(row?.facturacion_total) || 0;
        clientData[row.cliente_id].costs += parseFloat(row?.coste_salarial_total) || 0;
      });

      // Convert to array and sort by revenue
      const sortedData = Object.values(clientData)
        ?.sort((a, b) => b?.revenue - a?.revenue)
        ?.slice(0, limit);

      return sortedData;
    } catch (error) {
      console.error('Error fetching comparative data:', error);
      throw error;
    }
  },

  // Get quick summaries
  async getQuickSummaries(filters = {}) {
    try {
      const { dateFrom, dateTo } = resolveDateRange(filters);

      // Use rentabilidad_por_cliente_final view for most profitable client
      let rentabilidadQuery = supabase?.from('rentabilidad_por_cliente_final')?.select('cliente_id, cliente_nombre, facturacion_total, numero_loncheados');
      
      if (dateFrom && dateTo) {
        const dateFromStr = dateFrom?.toISOString()?.split('T')?.[0];
        const dateToStr = dateTo?.toISOString()?.split('T')?.[0];
        rentabilidadQuery = rentabilidadQuery?.gte('fecha', dateFromStr)?.lte('fecha', dateToStr);
      }

      const { data: rentabilidadData, error: rentabilidadError } = await rentabilidadQuery;
      if (rentabilidadError) throw rentabilidadError;

      // Aggregate client revenues from rentabilidad view
      const clientRevenues = {};
      let totalRevenue = 0;
      let totalLoncheados = 0;

      rentabilidadData?.forEach(row => {
        const clientId = row?.cliente_id;
        if (clientId) {
          if (!clientRevenues?.[clientId]) {
            clientRevenues[clientId] = {
              nombre: row?.cliente_nombre || 'Cliente Desconocido',
              revenue: 0
            };
          }
          const facturacion = parseFloat(row?.facturacion_total) || 0;
          clientRevenues[clientId].revenue += facturacion;
          totalRevenue += facturacion;
        }
        totalLoncheados += parseInt(row?.numero_loncheados) || 0;
      });

      const mostProfitableClient = Object.values(clientRevenues)?.sort((a, b) => b?.revenue - a?.revenue)?.[0] || null;

      // Get employee productivity from loncheados table
      let loncheadosQuery = supabase?.from('loncheados')?.select('empleado_id, peso_loncheado, empleados(nombre)');
      
      if (dateFrom && dateTo) {
        loncheadosQuery = loncheadosQuery?.gte('created_at', dateFrom?.toISOString())?.lte('created_at', dateTo?.toISOString());
      }

      const { data: loncheadosData, error: loncheadosError } = await loncheadosQuery;
      if (loncheadosError) throw loncheadosError;

      // Calculate employee productivity
      const employeeStats = {};
      let totalKg = 0;

      loncheadosData?.forEach(l => {
        const empleadoId = l?.empleado_id;
        if (empleadoId) {
          if (!employeeStats?.[empleadoId]) {
            employeeStats[empleadoId] = {
              nombre: l?.empleados?.nombre || 'Empleado Desconocido',
              loncheadosCount: 0
            };
          }
          employeeStats[empleadoId].loncheadosCount += 1;
        }
        totalKg += parseFloat(l?.peso_loncheado) || 0;
      });

      const mostProductiveEmployee = Object.values(employeeStats)?.sort((a, b) => b?.loncheadosCount - a?.loncheadosCount)?.[0] || null;

      // Calculate averages
      let daysInPeriod = 1;
      if (dateFrom && dateTo) {
        daysInPeriod = Math.ceil((dateTo - dateFrom) / (1000 * 60 * 60 * 24)) + 1;
      }

      const avgKgPerDay = totalKg / daysInPeriod;
      const avgRevenuePerLoncheado = totalLoncheados > 0 ? totalRevenue / totalLoncheados : 0;

      return {
        mostProfitableClient,
        mostProductiveEmployee,
        avgKgPerDay,
        avgRevenuePerLoncheado
      };
    } catch (error) {
      console.error('Error fetching quick summaries:', error);
      throw error;
    }
  }
};